// AI follow-up suggestion chips (Joy feedback PR 11).
//
// Renders the model's PROPOSED follow-ups under the note composer /
// logged-email row. One click approves (creates a real task via the
// backend — To Do sync rides Flow A) or dismisses; the title is
// editable inline before approving. Labeled as AI per the
// never-masquerade doctrine — the model proposes, the human decides.

import { useState } from "react";
import { Check, Pencil, Sparkles, X } from "lucide-react";

import { api, ApiError, type FollowupSuggestion } from "../../api";
import { formatDate } from "../../utils/formatters";

type ChipState = "proposed" | "working" | "approved" | "dismissed";

export function FollowupSuggestionChips({
  suggestions,
  onResolved,
}: {
  suggestions: FollowupSuggestion[];
  /** Called after every approve/dismiss (badge counts etc.). */
  onResolved?: (() => void) | undefined;
}) {
  if (suggestions.length === 0) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 10.5,
          fontWeight: 800,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--blue-dark)",
          marginBottom: 6,
        }}
      >
        <Sparkles size={12} strokeWidth={1.75} aria-hidden />
        Suggested follow-ups · AI
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {suggestions.map((s) => (
          <SuggestionChip key={s.id} suggestion={s} onResolved={onResolved} />
        ))}
      </div>
    </div>
  );
}

function SuggestionChip({
  suggestion,
  onResolved,
}: {
  suggestion: FollowupSuggestion;
  onResolved?: (() => void) | undefined;
}) {
  const [state, setState] = useState<ChipState>("proposed");
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(suggestion.suggestion_title);
  const [error, setError] = useState<string | null>(null);

  async function approve() {
    setState("working");
    setError(null);
    try {
      const edits =
        title.trim() !== suggestion.suggestion_title
          ? { title: title.trim() }
          : {};
      await api.approveFollowupSuggestion(suggestion.id, edits);
      setState("approved");
      onResolved?.();
    } catch (err) {
      setState("proposed");
      setError(err instanceof ApiError ? err.message : "Couldn't create the task.");
    }
  }

  async function dismiss() {
    setState("working");
    setError(null);
    try {
      await api.dismissFollowupSuggestion(suggestion.id);
      setState("dismissed");
      onResolved?.();
    } catch (err) {
      setState("proposed");
      setError(err instanceof ApiError ? err.message : "Couldn't dismiss.");
    }
  }

  if (state === "dismissed") return null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: state === "approved" ? "var(--success-bg)" : "var(--bg-1)",
        border: "1px solid var(--mid-gray)",
        borderRadius: "var(--radius-md)",
        padding: "6px 10px",
        fontSize: 12.5,
      }}
    >
      {state === "approved" ? (
        <>
          <Check size={14} strokeWidth={2} style={{ color: "var(--success)" }} aria-hidden />
          <span style={{ color: "var(--success)", fontWeight: 600 }}>
            Task created — {title}
          </span>
        </>
      ) : (
        <>
          {editing ? (
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={140}
              style={{
                flex: 1,
                fontSize: 12.5,
                padding: "3px 6px",
                border: "1px solid var(--mid-gray)",
                borderRadius: 6,
              }}
            />
          ) : (
            <span style={{ flex: 1, color: "var(--navy)" }}>
              {suggestion.household_display_name && (
                <span style={{ color: "var(--fg-muted)", fontWeight: 600 }}>
                  {suggestion.household_display_name} —{" "}
                </span>
              )}
              {title}
              {suggestion.suggested_due_date && (
                <span style={{ color: "var(--fg-muted)", fontSize: 11.5 }}>
                  {" "}
                  · due {formatDate(suggestion.suggested_due_date)}
                </span>
              )}
            </span>
          )}
          <button
            type="button"
            title={editing ? "Done editing" : "Edit before approving"}
            onClick={() => setEditing((e) => !e)}
            disabled={state === "working"}
            style={chipBtnStyle}
          >
            <Pencil size={13} strokeWidth={1.75} aria-hidden />
          </button>
          <button
            type="button"
            title="Approve — create the task"
            onClick={() => void approve()}
            disabled={state === "working" || title.trim().length < 3}
            style={{ ...chipBtnStyle, color: "var(--success)" }}
          >
            <Check size={14} strokeWidth={2} aria-hidden />
          </button>
          <button
            type="button"
            title="Dismiss"
            onClick={() => void dismiss()}
            disabled={state === "working"}
            style={{ ...chipBtnStyle, color: "var(--danger)" }}
          >
            <X size={14} strokeWidth={2} aria-hidden />
          </button>
        </>
      )}
      {error && (
        <span style={{ color: "var(--danger)", fontSize: 11 }}>{error}</span>
      )}
    </div>
  );
}

const chipBtnStyle: React.CSSProperties = {
  border: 0,
  background: "transparent",
  cursor: "pointer",
  padding: 2,
  display: "inline-flex",
  color: "var(--fg-muted)",
};
